import React from 'react';
import { Link } from 'react-router-dom';
import GuideShell, { StepCard, SectionHeader, TipCallout, InfoRow } from './guides/GuideShell';

/**
 * /guides/audience-prep — public, no auth required.
 *
 * Fan-facing walkthrough. Creators share the URL with their community
 * to prep them for launch day, and the same page is surfaced in-app
 * (Help Center + Clan detail first-visit banner). Uses everyday
 * language, no creator jargon.
 *
 * Structure — four parts:
 *   Onboarding (Steps 1–4)   Sign up → Wallet → Bookmark → Bell
 *   Part A — How to trade    Buy sheet → Trade screen → Sell flow
 *   Part B — Hunt Opportunities  Bookmarks · Perks · Bounties · Launches
 *   Part C — Real-money math     Aspirational $10 → $500 story on the curve
 *   Part D — Due diligence   Three crisp checks before you buy
 *
 * Screenshots for the new sections are pending — placeholders below
 * are labelled with the intended asset so we can drop them in as soon
 * as the creator supplies them.
 */
export default function GuideAudiencePrep() {
  return (
    <GuideShell
      slug="audience-prep"
      eyebrow="For fans"
      title="Your creator's launching — here's what to do 🎉"
      subtitle="A full walkthrough for new fans: how to sign up, load your Wallet, grab your first Bit, trade smart, and hunt the next launch. Also permanently pinned inside the app — open Help Center any time to reread it."
      accent="audience"
    >
      {/* ══════════════════════════════════════════════════════════════════
          PART 0 — Onboarding & launch-day (Steps 1–4)
          ══════════════════════════════════════════════════════════════════ */}
      <SectionHeader
        eyebrow="Getting ready"
        title="Before your creator's launch"
        kicker="Do these four things in the ~10 minutes before your favourite creator's Clan goes live."
      />

      <StepCard number={1} title="Sign up with your creator's link">
        <p>
          Use the referral link your creator shared — it's how they get credit for
          bringing you in (they earn a small cut of every trade you make, at no extra
          cost to you).
        </p>
        <p>Sign up takes ~2 minutes. You'll need an email and a handle.</p>
        <figure className="guide-figure">
          <img src="/guides/audience-1.png" alt="Clanster home showing the Get started checklist" loading="lazy" />
          <figcaption>Right after signup you'll land on Home — with a Get started checklist that walks you through the first few taps.</figcaption>
        </figure>
      </StepCard>

      <StepCard number={2} title="Load your Clanster Wallet">
        <p>
          Bits are priced in <b>Points</b> — Clanster's in-app currency. 1 Point = $1 USD.
        </p>
        <p>
          On the <b>Wallet</b> page tap <b>Deposit</b>. You'll pay with crypto (BTC, ETH,
          USDC, SOL and many more supported). Deposits usually settle in 2–30 minutes
          depending on the network.
        </p>
        <InfoRow>
          <div><b>Minimum:</b> $10 · <b>Maximum:</b> $50,000 per invoice</div>
          <div>No account limits, no KYC for deposits, no hidden fees.</div>
        </InfoRow>
        <TipCallout>
          Rule of thumb: deposit at least <b>3–5× the price of one Bit</b> so you can
          grab a few early on the curve, not just one.
        </TipCallout>
        <figure className="guide-figure">
          <img src="/guides/audience-2.png" alt="Wallet page — red arrow points at the Deposit button" loading="lazy" />
          <figcaption>Your Wallet — tap <b>Deposit</b> (arrow) to top up with crypto. 1 pt = $1 USD.</figcaption>
        </figure>
      </StepCard>

      <StepCard number={3} title="Bookmark your creator + hit the 🔔 Reminder Bell">
        <p>Two separate buttons — do both:</p>
        <ol>
          <li>
            <b>Bookmark</b> — saves the creator to your Bookmarks list in the avatar
            menu for one-tap access anytime.
          </li>
          <li>
            <b>🔔 Reminder Bell</b> — subscribes you to a launch-time alert. You'll get
            an email and push notification the exact second trading opens.
          </li>
        </ol>
        <figure className="guide-figure">
          <img src="/guides/audience-3.png" alt="Creator's Clan page — red arrow points at the Bookmark icon next to Join Clan" loading="lazy" />
          <figcaption>On any Clan page, tap the <b>bookmark icon</b> (arrow) next to Join Clan to save the creator to your list.</figcaption>
        </figure>
      </StepCard>

      <StepCard number={4} title="Launch day — early buyers pay less">
        <p>
          Bits sit on a <b>bonding curve</b>: earlier buyers pay less, later buyers pay
          more. Every buy nudges the next Bit's price up smoothly; every sell nudges it
          back down. There's no fixed supply and no sold-out state.
        </p>
        <TipCallout tone="win">
          <b>Early buyers grab the cheapest Bits.</b> If your creator scheduled the
          launch, you'll see a live countdown timer on their Clan page — be there when
          it hits zero for the best entry price.
        </TipCallout>
        <p>
          <b>Late to the party? Still worth showing up.</b> You unlock the <i>exact
          same</i> private Feed, Live streams, Chat and Perks as day-one holders. And
          you can still climb the top-holder leaderboard, earn Milestone Airdrops,
          complete Bounties, and redeem Perks the creator drops over time.
        </p>
        <figure className="guide-figure">
          <img src="/guides/audience-4.png" alt="Live Clan page — price, holders, and the Join Clan / Buy Bit buttons" loading="lazy" />
          <figcaption>The creator's live Clan page — tap <b>Buy Bit</b> to open the trade sheet.</figcaption>
        </figure>
      </StepCard>

      {/* ══════════════════════════════════════════════════════════════════
          PART A — How to actually trade
          ══════════════════════════════════════════════════════════════════ */}
      <SectionHeader
        eyebrow="Part A"
        title="How to actually trade — buy, hold, sell"
        kicker="A guided tour of the Buy sheet, Trade screen and Sell flow so nothing surprises you the first time you tap Buy."
      />

      <StepCard number={5} title="Open the Buy sheet from any Clan page">
        <p>
          On the creator's live Clan page, tap <b>Buy Bit</b> in the action row. A slide-up
          sheet opens with everything you need to price the trade before you commit:
        </p>
        <ul>
          <li><b>Your Balance / You Own</b> right at the top — no math needed</li>
          <li><b>Current price</b> (updates every 3 seconds) and <b>Price after buy</b> side by side so you can see the curve step</li>
          <li><b>Bits to Buy</b> stepper with 1 / 10 / 100 / 1,000 / Max quick-picks</li>
          <li>A subtle reminder if you need <b>N Bits to enter this Clan Feed</b> — so you know exactly how many to grab</li>
          <li>Full breakdown: cost + 10% fees (5% to creator, 5% to Clanster) + <b>You pay</b> total</li>
          <li><b>What your N Bits could become</b> — a collapsible projection card</li>
          <li><b>Max slippage</b> pill (Auto / +2% / +5% / +10%) protects you if the price moves while you tap</li>
        </ul>
        <p>
          Confirm the trade and Bits land in your holdings <b>instantly</b>. So does access
          to the creator's private Feed, Live, Chat and Perks.
        </p>
        <figure className="guide-figure">
          <img src="/guides/audience-5.png" alt="Buy sheet showing balance, current price, price-after-buy, Bits stepper, fee breakdown and slippage picker" loading="lazy" />
          <figcaption>The Buy sheet — everything priced <i>before</i> you tap the big pink <b>Buy N Bits</b> button.</figcaption>
        </figure>
      </StepCard>

      <StepCard number={6} title="Read the Trade screen like a pro">
        <p>
          Tap <b>Trade</b> in the top nav for the full trading view. From here you can
          watch every Clan's price in real time and jump between them fast:
        </p>
        <ul>
          <li><b>Trending marquee</b> up top — Clans with the biggest 24-hour moves scroll across</li>
          <li><b>Search bar</b> — jump straight to any creator by name or @handle</li>
          <li><b>Active Clans strip</b> — creators you already hold, so you can rebalance without hunting</li>
          <li><b>Live chart</b> with <b>Live / 24H / 7D / All</b> tabs — shows every trade as it happens (empty state reads <i>"Waiting for the first trades…"</i> until buys start)</li>
          <li><b>Stats row</b> — your <b>Holdings</b>, the Clan's <b>Min</b> to unlock the Feed, total <b>Minted</b>, and your <b>Wallet</b> balance in Points</li>
          <li><b>Buy / Sell quick-picks</b> — Buy row (1 / 10 / 50 / MAX) and Sell row (25% / 50% / 75% / MAX) let you fire off any-size trade in two taps</li>
          <li><b>Total</b> + <b>Slippage</b> display at the bottom, then the big <b>SELL / BUY</b> buttons</li>
        </ul>
        <TipCallout>
          Green momentum chips in the trending marquee (<b>Nina Osei ▲ 276%</b>) and the pulsing
          <b> ● LIVE </b> dot mean the creator is actively posting and their holders are
          actively trading — those are the Clans where price moves fastest in both directions.
        </TipCallout>
        <figure className="guide-figure">
          <img src="/guides/audience-6.png" alt="Full Trade screen — trending marquee, search, active clans, chart, stats row, buy/sell quick-picks, big Buy/Sell buttons" loading="lazy" />
          <figcaption>The Trade screen — one dashboard for the whole market, plus one-tap Buy/Sell for every Clan you hold.</figcaption>
        </figure>
      </StepCard>

      <StepCard number={7} title="Sell whenever you like — no lockup">
        <p>
          Selling works the same way in reverse. Open the creator's Clan page or the
          Trade screen and switch the toggle to <b>Sell</b>:
        </p>
        <ul>
          <li>Choose how many Bits to sell (any amount from 1 up to your full holding)</li>
          <li>See the exact Points you'll <b>receive</b> after fees — <i>before</i> you confirm</li>
          <li>Peek at <b>What you could earn if you hold on</b> — a projection card that shows the price a couple of milestones out, so you don't sell 15 seconds before the run</li>
          <li>Confirm and Points land in your Wallet instantly</li>
        </ul>
        <p>
          Every sell moves the curve down by the same step-size a buy moves it up.
          There's no cooldown, no minimum hold period, no "get out" penalty. You can
          exit fully or partially any time.
        </p>
        <TipCallout tone="warn">
          <b>Selling below the min-to-join floor</b> drops you out of the private Feed,
          Live and Chat. You can always re-buy to unlock them again — but you'll pay the
          current curve price, not what you originally paid.
        </TipCallout>
        <figure className="guide-figure">
          <img src="/guides/audience-7.png" alt="Sell sheet — Sell toggle active, price after sell shown, You receive total, hold-on projection card, slippage picker" loading="lazy" />
          <figcaption>The Sell sheet — payout previewed <i>before</i> you confirm, and a "hold on?" card that shows what you'd earn if you waited.</figcaption>
        </figure>
      </StepCard>

      {/* ══════════════════════════════════════════════════════════════════
          PART B — Hunt opportunities
          ══════════════════════════════════════════════════════════════════ */}
      <SectionHeader
        eyebrow="Part B"
        title="Hunt opportunities — Bookmarks, Perks, Bounties, Launches"
        kicker="Trading well is only half the game. The other half is finding the next opportunity before the crowd does."
      />

      <StepCard number={8} title="Bookmark aggressively — build your watchlist">
        <p>
          Anywhere you find a creator you might trade — a Clan page, a Launches card, a
          Trending marquee entry — tap the <b>bookmark icon</b> next to their <b>+ Join
          Clan</b> button. Everything you bookmark rolls up under{' '}
          <b>Avatar menu → Bookmarks</b> — your personal opportunity radar. Use it to:
        </p>
        <ul>
          <li>Track creators <i>before</i> they launch (so you don't miss the countdown)</li>
          <li>Keep an eye on Clans where you're not yet a holder but might buy soon</li>
          <li>Compare price movement across the creators you follow, side by side</li>
        </ul>
        <p>Bookmark early, bookmark often — it costs nothing and it's the fastest way to build a personal radar. If a bookmarked Clan starts moving, you'll spot it before the crowd does.</p>
        <figure className="guide-figure">
          <img src="/guides/audience-12.png" alt="Avatar dropdown menu with the Bookmarks item highlighted" loading="lazy" />
          <figcaption>Open your <b>Avatar menu</b> (top-right) and tap <b>Bookmarks</b> to jump into your personal watchlist any time.</figcaption>
        </figure>
      </StepCard>

      <StepCard number={9} title="Chase Perks — the fastest way to get real value out of a Clan">
        <p>
          Perks are creator-set rewards you can spend Points on: 1-on-1 call time,
          custom art, presets, shoutouts, physical merch, priority DMs, and much more.
          They live under the <b>Perks</b> tab on any Clan you hold Bits in.
        </p>
        <ul>
          <li><b>Limited-quantity Perks</b> often sell out fast — the newest ones drop into your holdings feed</li>
          <li>Some Perks are <b>holder-only</b> (you need N Bits to unlock), others are open</li>
          <li>100% of Perk revenue goes to the creator — you're paying them directly for something real</li>
        </ul>
        <TipCallout>
          Perks are how you convert paper gains into <b>real experiences</b>. If your
          creator's Bit price has climbed since you bought in, you can sell a couple of
          Bits and immediately redeem a high-tier Perk with the profit.
        </TipCallout>
        <figure className="guide-figure">
          <img src="/guides/audience-9.png" alt="Perks tab inside a Clan — three perk cards with pts prices, Bits required, and claim counts" loading="lazy" />
          <figcaption>The Perks tab inside any Clan — every card shows the Points cost, the Bits required to unlock it, and how many claims are left.</figcaption>
        </figure>
      </StepCard>

      <StepCard number={10} title="Complete Bounties — earn Points, no capital needed">
        <p>
          Bounties are quests creators post to grow their audience — things like "make a
          short-form video clip", "like &amp; share my new post", "get first-7 comments",
          or "fan art challenge". Complete them, submit proof, and Points land in your
          Wallet on approval.
        </p>
        <p>
          Find them in two places: the <b>Bounties for you</b> section on your <b>Home</b>
          feed (curated for the Clans you already hold), and the full <b>Bounties</b> tab
          in the top nav for everything open across the platform. Each card shows:
        </p>
        <ul>
          <li>The creator + title + full brief</li>
          <li>The <b>pts / winner</b> reward and how many <b>slots</b> are still open (e.g. <i>5 / 5 slots</i>)</li>
          <li>Time-sensitive tag (<b>1d left</b>) when a Bounty is about to close</li>
          <li>Filter chips at the top — <b>Newest / Ending soon / Best reward</b> — to sort the way you hunt</li>
        </ul>
        <p>Some Bounties are <b>holder-only</b>, others are open to everyone. Points earned can buy more Bits, redeem Perks, or withdraw straight to crypto.</p>
        <TipCallout>
          If you're time-rich but capital-light, Bounties are the fastest on-ramp — you're
          literally being paid to help your creator grow, and every win pushes you higher
          on the leaderboard for the next Airdrop.
        </TipCallout>
        <figure className="guide-figure">
          <img src="/guides/audience-10.png" alt="Home feed showing the Bounties for you section — 4 open bounty cards with pts/winner rewards, slot counts, and a Newest/Ending soon/Best reward sort" loading="lazy" />
          <figcaption>The <b>Bounties for you</b> section on Home — curated by the Clans you hold. Sort by Newest / Ending soon / Best reward to hunt smart.</figcaption>
        </figure>
      </StepCard>

      <StepCard number={11} title="Prep for upcoming launches — arrive early, always">
        <p>
          Tap <b>Launches</b> in the top nav for every Clan scheduled to open trading
          soon. Three tabs let you filter the list: <b>Upcoming</b>, <b>Just launched</b>,
          and <b>All</b>. Every card shows:
        </p>
        <ul>
          <li>A live <b>countdown</b> in the corner — <i>in 50m</i>, <i>in 13d 2h</i>, etc.</li>
          <li>The creator's <b>opening Bit price</b> (e.g. <b>1.70 pts / Bit</b>)</li>
          <li>How many fans already hit the 🔔 bell (<b>0 reminded</b>, <b>147 reminded</b>, etc.) — a rough social-proof gauge</li>
          <li>Tap the card to open the creator's landing page, ring the bell, and bookmark them</li>
        </ul>
        <p>Your pre-launch checklist:</p>
        <ol>
          <li><b>Bookmark</b> every upcoming creator you care about</li>
          <li><b>Ring the bell</b> on the ones you plan to actually buy at t=0 — you'll get an email + push notification the second trading opens</li>
          <li><b>Deposit early</b> — crypto deposits take 2–30 minutes to settle, so top up the day before, not five minutes before launch</li>
          <li><b>Do your homework</b> (see Part D below)</li>
          <li><b>Be there at t=0</b> — early buyers grab the cheapest Bits and often ride the biggest curve moves</li>
        </ol>
        <figure className="guide-figure">
          <img src="/guides/audience-11.png" alt="Launches page — Upcoming tab, two clan cards with countdown timers (in 50m, in 13d 2h), reminded counts, and opening Bit prices" loading="lazy" />
          <figcaption>The <b>Launches</b> page — every upcoming Clan with a live countdown, reminded-count, and opening Bit price.</figcaption>
        </figure>
      </StepCard>

      {/* ══════════════════════════════════════════════════════════════════
          PART C — Real-money math (aspirational, honest)
          ══════════════════════════════════════════════════════════════════ */}
      <SectionHeader
        eyebrow="Part C"
        title="What can you actually make? The math, honestly."
        kicker="Clanster's bonding curve rewards early buyers dramatically — but only if the Clan grows. Here's the shape."
      />

      <StepCard number={12} title="The curve — early is exponentially cheaper">
        <p>
          Clanster's bonding curve is designed so that <b>doubling the number of Bits sold
          multiplies the price by roughly 1.6×</b> — not 2×. That means the earlier you're
          in, the wider the gap between what you paid and what a later buyer will pay.
        </p>
        <p>
          Take a Clan with a $0.25 opening Bit price. Here's what one Bit costs at each
          milestone:
        </p>
        <div className="guide-curve-table" data-testid="audience-curve-table">
          <div className="guide-curve-row guide-curve-head">
            <div>Bits sold</div><div>Price per Bit</div><div>Multiple vs. Bit #1</div>
          </div>
          <div className="guide-curve-row"><div>1 (launch)</div><div>$0.25</div><div>1×</div></div>
          <div className="guide-curve-row"><div>100</div><div>~$5.40</div><div>~22×</div></div>
          <div className="guide-curve-row"><div>1,000</div><div>~$25</div><div>~100×</div></div>
          <div className="guide-curve-row"><div>10,000</div><div>~$116</div><div>~460×</div></div>
          <div className="guide-curve-row guide-curve-highlight"><div>100,000</div><div>~$540</div><div>~2,160×</div></div>
        </div>
        <TipCallout tone="win">
          Buy 10 Bits at launch for <b>$2.50</b>. If that Clan rides all the way to
          100,000 Bits, those same 10 Bits are worth <b>~$5,400</b> at the curve price
          — <b>a 2,000× move</b>. That's the aspirational case, and it's real math.
        </TipCallout>
        <TipCallout tone="warn">
          <b>Not every Clan gets to 100K.</b> Most stop somewhere between a few
          hundred and a few thousand Bits, which is still a meaningful move for early
          buyers (10×–100×). And prices <i>can and do fall</i> — if holders sell faster
          than new buyers arrive, the curve moves the other way. Never spend more than
          you can afford to lose.
        </TipCallout>
      </StepCard>

      {/* ══════════════════════════════════════════════════════════════════
          PART D — Due diligence
          ══════════════════════════════════════════════════════════════════ */}
      <SectionHeader
        eyebrow="Part D"
        title="Before you buy — three quick checks"
        kicker="Not every launch is the next 100K Clan. Spend 3 minutes on these three checks and you'll dodge the obvious duds."
      />

      <StepCard number={13} title="Do your homework — three checks, that's it">
        <ul className="guide-dd-list">
          <li>
            <b>Real audience?</b> Open the creator's linked X / YouTube / IG. Look at the
            <i> last 30 days</i>: post cadence, comment quality, real replies vs. bot
            noise. A creator with 500 engaged fans beats one with 50,000 ghosts.
          </li>
          <li>
            <b>Do they deliver?</b> Scroll their Feed and Perks tab. Are they shipping
            posts and honouring redeemed Perks? A silent Feed 2 weeks after launch is the
            single biggest red flag.
          </li>
          <li>
            <b>Curve health.</b> On the Trade screen, glance at their price chart and
            recent trades. Steady buys from many wallets = healthy. One wallet pumping
            price alone = walk away.
          </li>
        </ul>
        <TipCallout>
          Green on all three = worth a starter position. Red on any one = wait, watch,
          or skip. Nothing on Clanster locks you out for waiting a day.
        </TipCallout>
      </StepCard>

      <div className="guide-cta">
        <div className="guide-cta-title">Ready to grab your first Bit?</div>
        <div className="guide-cta-sub">
          Everything above is inside the app once you sign up.
          This guide is permanently pinned under <b>Help Center</b> — reread it any time.
        </div>
        <Link to="/signup" className="guide-cta-btn" data-testid="guide-signup-btn">
          Open Clanster →
        </Link>
      </div>
    </GuideShell>
  );
}
